/**
 * ログイン直後の画面（ホーム・pages/HomeStart.tsx）の英語の文言（2026-09-28 creator/home-redesign）。en.ts が `...enHome` で取り込む
 * （ja.home.ts が同じキーを全部持つことを型で強制）。
 *
 * 言葉の決め事（founder の感想・最優先）: 「機能を選ぶ」ではなく「もう全部ある・AI エージェントに頼めばいい」。
 *   製品名（Claude Code・Codex・Cursor）は「つなぐ」の段の中（home.step.connect.* / home.agent.*）にだけ出す（founder 2026-09-28「Codex や AI エージェントなら何でも」）。
 *   wording.test.ts が、それ以外のキーに製品名が入らないことを止める
 *   このファイルには feature / module / enable（機能・モジュール・有効化）を書かない（wording.test.ts が止める）。
 *   ON/OFF・利用料の内訳・鍵・設定の話もしない（それはプロジェクトの画面の奥と「お金」の画面にある）。
 * WHY 別ファイルか: 最初の画面の言葉だけをまとめて見直せる・検査できるように
 */
export const enHome = {
  'home.eyebrow': 'Your backend',
  // A の見出し（2026-09-28 creator/plain-words: 決め台詞「Everything is already here.」を、何が用意されているかの文に）
  'home.title': 'Publishing, data and money are set up for your projects.',
  'home.lead': 'Ask your AI agent for what you want to make. It builds the project on these three.',
  'home.titleWithProjects': 'Your projects',

  // 3 つの柱（どれも「用意済み」として見せる）
  'home.pillarsLabel': 'What your projects already have',
  'home.ready': 'Ready',
  'home.pillar.publish.title': 'Publish',
  'home.pillar.publish.body': 'A home for your project on the web, with your own domain.',
  'home.pillar.data.title': 'Data',
  'home.pillar.data.body': 'A database and your own server code (Functions). Any table, any SQL.',
  'home.pillar.money.title': 'Money',
  'home.pillar.money.body': 'Take payments and withdraw what you earn.',
  // 課金・売上・出金を見せない間の同じ欄（2026-09-29 creator/launch-money・launchHidden.ts）
  'home.pillar.money.bodyLaunch': 'Your prepaid balance and the usage fees your projects run up.',

  // 次の一歩（段の並び）
  'home.stepsTitle': 'Your first steps',
  'home.nextTitle': 'Next step',
  'home.stepDone': 'Done',
  'home.step.verify.title': 'Confirm your email',
  'home.step.verify.body': 'Open the link we sent to {email}. Then you can connect your AI agent.',
  // 招待クレジットが確認待ち（homeFlow.ts inviteVerifyAmount・2026-09-29 launch-close-2）: 入金の段は出さず、確認すれば入ることをここで言う
  'home.step.verify.bodyInvite': 'Open the link we sent to {email}. Once you confirm, {amount} of invite credit is added (no top-up needed) and you can connect your AI agent.',
  'home.step.connect.title': 'Connect your AI',
  // どの AI にも貼る 1 つの文の上下（2026-10-11 console/one-connect・HomeParts.tsx ConnectBody）。文そのものは apps/site/src/connectPrompt.mjs（ここには書かない）。
  // 以前の頼み文（ask.*）・使い方の選び方（way.*）・Web 向け（web.*）・エージェントのタブ（home.agent.*）は同じ日に消した
  'home.step.connect.copyLabel': 'Copy this and paste it into your AI',
  // 2026-10-18 gtm/early-fixes（直す案 9）: 前は「Web やスマホの Claude・ChatGPT でも使える」= メール E3「the phone apps can't add it yet」と逆。
  //   事実はつなぐ文の正本（connectPrompt.mjs webGuide「The phone apps cannot add it (add it in a computer browser; then it works on the phone too)」）。
  //   スマホのアプリで試して失敗して去る道を作らない
  'home.step.connect.anyAi': 'Works in Claude Code, Codex, Cursor, and Claude or ChatGPT on the web (use a computer browser).',
  // 貼る文の下の小さなリンク（2026-10-25 site/start-page・webConnect.ts startPageUrl）。人が読む、使っている環境ごとの手順のページ
  'home.step.connect.startPage': 'New here? See the steps for where you use AI',
  'home.step.connect.noPrompt': 'Look for “Use from an AI agent (MCP)” or “Use in Claude or ChatGPT on the web” in the {product} docs.',
  'home.step.topUp.title': 'Add a balance',
  // 招待コードのある人が「まず $5 払う」と読まないように、同じ画面で入れられることを添える（2026-09-29 launch-close-2・Money の前払いの道 17）
  //   2026-10-29 core/open-signup-polish（Rauno 役）: 「招待コードがあれば同じ画面で」の 1 文を外した。すぐ下に「Have an invite code? Enter it」
  //   （invite.haveCode・欄へ直に飛ぶ）を置いたので、文とリンクが同じことを二度言っていた。この文を使う 3 か所（HomeC・HomeParts の段と SetupNotice）はどれもリンクを持つ
  'home.step.topUp.body': 'You pay for what you use, at cost, from this balance. Once there is a balance, your agent can create and publish your project.',
  'home.step.topUp.cta': 'Add money',
  'home.step.talk.title': 'Ask your AI agent',
  'home.step.talk.body': 'Say it in your own words. For example:',
  'home.example.1': 'Make a booking app that takes reservations, and publish it.',
  // 2026-09-29 creator/launch-money（管理・Quality の試運転の指摘）: 以前は「有料の会員サイト」= 初回で閉じている利用者への販売を勧める例だった。
  // 初回の範囲（ログイン・DB・公開）の例に差し替えた。課金を再開したら戻してよい
  'home.example.2': 'Make a shared to-do list with sign-in that saves to a database.',
  'home.example.3': 'Make an idle clicker game with saves, and publish it.',
  // 4 つ目 = オンライン対戦（MASTER-PLAN §0「マルチプレイを 4 つ目の言葉として前に出す」founder 2026-10-02）。
  // 部屋・クイックマッチ・つなぎ直しは実装済みで初回から開いている。「クイックマッチ」を文に入れて、エージェントが Kumo のマッチングを使う道に乗るようにする
  'home.example.4': 'Make a quiz game people can play against each other online with quick match, and publish it.',
  'home.step.publish.title': 'Publish',
  'home.step.publish.body': 'Ask “publish it” and your project gets a URL. You can add your own domain later. Published projects show up here.',

  // プロジェクトの一覧・作成の入口
  'home.newProject': 'New project',
  'home.createHere': 'Or create a project here',
  'home.projectsEmpty': 'No projects yet. The ones your agent makes will show up here.',
  // ------------------------------------------------------------ B（問いかけの見出し・homeFlow.ts HOME_VARIANT）
  // 見出し・入力欄・頼み方の文。{what} = 入力（homeFlow.ts buildAsk が整えて入れる。括りの "…" はここ側）
  'home.b.title': 'What will you make?',
  'home.b.placeholder': 'A booking app that takes reservations',
  'home.b.examplesLabel': 'Try one:',
  'home.askExample.1': 'A booking app that takes reservations',
  'home.askExample.2': 'A shared to-do list with sign-in',
  'home.askExample.3': 'An idle clicker (game)',
  // 4 つ目 = オンライン対戦（MASTER-PLAN §0「マルチプレイを 4 つ目の言葉として前に出す」founder 2026-10-02）。
  // 部屋・クイックマッチ・つなぎ直しは実装済みで初回から開いている。「クイックマッチ」を文に入れて、エージェントが Kumo のマッチングを使う道に乗るようにする
  'home.askExample.4': 'A quiz game people play against each other online, with quick match',
  // 最初の 1 つの頼み文（HomeC の「頼む」の段の一番上・2026-10-14 gtm/onboarding）。活性化 =「最初のゲームがテスト版の URL で動いた」に
  // まっすぐ向かう 1 文。つなぐ文（apps/site/src/connectPrompt.mjs）の最後「Make a small game with … and publish it.」と同じ頼みにそろえる
  // （AI が「次はこう頼んで」と言った文と画面の文が同じ = 迷わない）。下の 4 つの例は「ほかの例」として残す（作り手が選べる）
  'home.askExample.first': 'Make a small game with {product} and publish it. Send me the URL so I can play it.',
  // 広告から来た人（adVisit.ts の印）の最初の頼み（2026-10-18 gtm/early-fixes・GTM の確認 early-review.md の直す案 2）。広告の約束「"publish this game"」と同じ。
  //   その人はもうゲームを持っている = 新しく作らせない。まだ無い人向けに、今までの頼みを下に 1 行（noGameYet）。つなぐ文の締め（connectPrompt.mjs askLine）と同じ文
  'home.askExample.firstAd': 'Publish this game with {product}. Send me the URL so I can play it.',
  'home.askExample.noGameYet': 'No game yet? Ask: "Make a small game with {product} and publish it."',
  'home.askTemplate': 'Build "{what}" on {product}. Use {product} for sign-in, the database and publishing.',
  'home.b.askLabel': 'Paste this into your AI agent',
  'home.b.copy': 'Copy',
  'home.b.readyLine': 'Publishing, data and money are ready.',
  'home.b.connectLabel': 'Connect your AI agent once',
  'home.b.connected': 'Your AI agent is connected.',
  'home.b.setupLabel': 'Before you start',


  // ------------------------------------------------------------ C（既定・場所の名前だけの素直な画面・homeFlow.ts HOME_VARIANT）
  // 決め台詞・宣伝の言葉を書かない。どれも「何が起きるか」をそのまま書く（wording.test.ts が止める言い回しあり）
  'home.c.title': 'Projects',
  'home.c.empty': 'No projects yet. Connect your AI and ask it to make one, or create one here.',
  'home.c.stepsLabel': 'Steps',
  'home.c.connectBody': 'Once connected, the AI you use can create, change and publish your projects on {product}.',
  'home.c.askTitle': 'Ask your AI agent',
  'home.c.askBody': 'Copy an example and paste it into your agent. It builds the project on {product} and can publish it.',
  'home.c.examplesLabel': 'Example requests',
  'home.c.create': 'Create project',
  'home.c.setupTitle': 'Still to set up',
  // ---- 2026-10-14 gtm/onboarding（いまの 1 段だけ開く・つながったかが分かる・最初の頼み文 1 つ）HomeC.tsx
  // 確認の段を開いている間: 別のタブでリンクを開けば、この画面は自分で次へ進む（ProjectsPage が /v1/developers/me を読み直す）
  'home.c.verifyWaiting': 'After you open the link, this page moves on by itself.',
  // つなぐの段を開いている間: 文を貼ったあと「つながったのか」が分からず止まらないように（委任を 5 秒ごとに読み直す）
  'home.c.connectWaiting': 'Waiting for your AI to connect. This page updates by itself when it does.',
  // つないだ段の「済み」の代わり（何が済んだかを言う）
  'home.c.connected': 'Connected',
  // 2026-11-05 core/first5: CLI のログインだけでつながった（MCP の許可はまだ・homeFlow.ts connectedFrom の via 'cli'）ときに済みの段の下へ 1 行。
  //   WHY: CLI で作る・公開するのはもうできる（済みは正しい）。ただ `connect` が足した MCP は AI を再起動して許可するまで使えない = 次の一手を言う
  //   最初の案の「… and can start now.」は外した: $0 の人はこの下の段が「Add a balance」= 「もう始められる」と食い違う（Rauno 役の確認）
  'home.c.connectedCli': 'Your AI signed in from the command line. To also give it the {product} tools, restart your AI and allow {product} when it asks.',
  'home.c.firstAskLabel': 'Start with this',
  // 事実: AI の公開はまずテスト版（--dev）。みんなに公開する前に一度だけ確認が出る（guide.start.publish.body と同じ言い方・plainWords.test.ts で「本番」は使わない）
  'home.c.firstAskNote': 'It goes on a test version first, with a link to try it. Before it goes public, you are asked once to confirm.',
  'home.c.moreExamplesLabel': 'Or ask for something else',
  // 最初のテスト版のカード（HomeC.tsx FirstTestCard・2026-10-15 gtm/onboarding）。事実: テスト版はリンクを知っている人だけが開ける場所、
  // みんなに公開するのは AI に頼んで一度だけ確認してから（guide.start.publish.body と同じ言い方・「本番」「デプロイ」は使わない）
  'home.c.firstTest.title': 'Your test version is up',
  'home.c.firstTest.play': 'Open it',
  'home.c.firstTest.next': 'When you are happy with it, tell your AI:',
  'home.c.firstTest.ask': 'Publish it so everyone can open it.',
  'home.c.firstTest.note': 'Before it goes public, you are asked once to confirm.'
} as const;
